// Реалтайм кабинета Оптовика: общий чат с курьерами и статусы курьеров.
// Один Socket.IO на весь кабинет — счётчик в меню, лента чата и доска «На линии»
// слушают одно соединение.

import { io, type Socket } from 'socket.io-client';

import { apiGet, refreshAccessToken } from '@/lib/api';
import { useAuthStore } from '@/store/auth.store';
import { useChatStore } from './store';
import { getChatMeId } from './me';

export { useChatStore } from './store';

export type ChatAttachment =
  | { type: 'photo'; url: string; width: number | null; height: number | null }
  | { type: 'voice'; url: string; mime: string; durationMs: number; waveform: number[] };

/** Цитата в ответе: кто и что написал (текст сервер уже укоротил). */
export interface ChatReply {
  id: string;
  authorId: string | null;
  authorName: string;
  text: string;
  attachment: { type: 'photo'; url: string } | { type: 'voice'; durationMs: number } | null;
  deleted: boolean;
}

export type ChatRole = 'owner' | 'courier';

export interface ChatMessage {
  id: string;
  /** У фото и голосовых — подпись или пусто. */
  text: string;
  createdAt: string;
  author: { id: string | null; name: string; role: ChatRole; avatarUrl?: string | null };
  attachment?: ChatAttachment | null;
  /** Удалено автором у всех: в ленте серая плашка «Сообщение удалено». */
  deleted?: boolean;
  replyTo?: ChatReply | null;
}

/** Событие курьера для доски «На линии»: начал/завершил смену или закрыл точку. */
export interface CourierEvent {
  type: 'shift-started' | 'shift-finished' | 'visit-closed' | 'visit-reopened';
  courierId: string;
  shiftId: string;
}

/** Удалено у всех: на месте сообщения — плашка, в цитатах ответов на него — тоже. */
export function markDeleted(messages: ChatMessage[], id: string): ChatMessage[] {
  return messages.map((m) => {
    if (m.id === id) return { ...m, text: '', attachment: null, replyTo: null, deleted: true };
    if (m.replyTo?.id === id) return { ...m, replyTo: { ...m.replyTo, text: '', attachment: null, deleted: true } };
    return m;
  });
}

const SOCKET_URL = import.meta.env.VITE_API_URL?.trim() || window.location.origin;
const MAX_AUTH_RETRIES = 3;

let socket: Socket | null = null;
let chatOpen = false;
let authRetries = 0;
const messageListeners = new Set<(m: ChatMessage) => void>();
const deleteListeners = new Set<(id: string) => void>();
const hideListeners = new Set<(id: string) => void>();
const courierListeners = new Set<(e: CourierEvent) => void>();
const locationListeners = new Set<(e: CourierLocation) => void>();

export async function refreshUnread(): Promise<void> {
  try {
    const { unread } = await apiGet<{ unread: number }>('/chat/unread', { silent: true });
    useChatStore.getState().setUnread(unread);
  } catch {
    /* следующая попытка — при переподключении сокета */
  }
}

function chatVisible(): boolean {
  return chatOpen && document.visibilityState === 'visible';
}

export function connectRealtime(): void {
  if (socket) return;
  // Только websocket: за nginx несколько web-инстансов без sticky-сессий,
  // long-polling между ними терял бы сессию.
  const s = io(SOCKET_URL, {
    path: '/socket.io',
    transports: ['websocket'],
    reconnectionDelay: 1_000,
    reconnectionDelayMax: 8_000,
    auth: (cb) => cb({ token: useAuthStore.getState().accessToken ?? '', kind: 'wholesaler' }),
  });
  socket = s;

  s.on('connect', () => {
    authRetries = 0;
    if (chatVisible()) s.emit('wh-chat:presence', { open: true });
    void refreshUnread();
  });

  // После отказа middleware socket.io сам не переподключается: access истёк —
  // обновляем сессию и пробуем снова, но не бесконечно.
  s.on('connect_error', () => {
    if (s.active || authRetries >= MAX_AUTH_RETRIES) return;
    authRetries += 1;
    void refreshAccessToken().then((token) => {
      if (token && socket === s) window.setTimeout(() => s.connect(), 1000 * authRetries);
    });
  });

  s.on('wh-chat:message', (m: ChatMessage) => {
    if (m.author.id !== getChatMeId() && !chatVisible()) {
      const { unread, setUnread } = useChatStore.getState();
      setUnread(unread + 1);
    }
    messageListeners.forEach((fn) => fn(m));
  });

  s.on('wh-chat:delete', (p: { id: string }) => {
    deleteListeners.forEach((fn) => fn(p.id));
    if (!chatVisible()) void refreshUnread();
  });

  s.on('wh-chat:hide', (p: { id: string }) => {
    hideListeners.forEach((fn) => fn(p.id));
  });

  s.on('courier:location', (e: CourierLocation) => {
    locationListeners.forEach((fn) => fn(e));
  });

  s.on('courier:event', (e: CourierEvent) => {
    courierListeners.forEach((fn) => fn(e));
  });
}

export function disconnectRealtime(): void {
  socket?.disconnect();
  socket = null;
  chatOpen = false;
  useChatStore.getState().setUnread(0);
}

/** Чат открыт и виден — сервер не шлёт этому участнику пуш. */
export function setChatPresence(open: boolean): void {
  chatOpen = open;
  socket?.emit('wh-chat:presence', { open: open && document.visibilityState === 'visible' });
}

export function onChatMessage(fn: (m: ChatMessage) => void): () => void {
  messageListeners.add(fn);
  return () => messageListeners.delete(fn);
}

export function onChatDelete(fn: (id: string) => void): () => void {
  deleteListeners.add(fn);
  return () => deleteListeners.delete(fn);
}

export function onChatHide(fn: (id: string) => void): () => void {
  hideListeners.add(fn);
  return () => hideListeners.delete(fn);
}

export function onCourierEvent(fn: (e: CourierEvent) => void): () => void {
  courierListeners.add(fn);
  return () => courierListeners.delete(fn);
}

/** Курьер на смене сдвинулся: последняя позиция (не чаще раза в 10 секунд). */
export interface CourierLocation {
  shiftId: string;
  courierId: string;
  lat: number;
  lon: number;
  accuracy: number | null;
  at: string;
}

export function onCourierLocation(fn: (e: CourierLocation) => void): () => void {
  locationListeners.add(fn);
  return () => locationListeners.delete(fn);
}
